import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Skeleton,
  DialogContent,
  Dialog,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { colors, fonts, size } from "../ColorFont/color";
import PaymentPopup from "./PaymentPops/PaymentPopup";
import { url } from "../ConstUrl/Url";
import SignUp from "../auth/SignUp";
import { t } from "i18next";

const PlansPricing = ({ userData, setUserData }) => {
  const role = userData?.role;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [buyerPlans, setBuyerPlans] = useState([]);
  const [sellerPlans, setSellerPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isBuyer, setIsBuyer] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fetchBuyerPlans = async () => {
      try {
        const response = await fetch(`${url}/api/plans`);
        const data = await response.json();
        setBuyerPlans(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchSellerPlans = async () => {
      try {
        const response = await fetch(`${url}/api/get-seller-plans`);
        const data = await response.json();
        setSellerPlans(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBuyerPlans();
    fetchSellerPlans();
  }, []);

  useEffect(() => {
    if (role === "Buyer") {
      setIsBuyer(true);
    } else if (role === "Seller") {
      setIsSeller(true);
    } else {
      setIsBuyer(true);
    }
  }, [role, isPopupOpen]);

  const handleChoosePlan = (plan, type) => {
    setSelectedPlan(plan);
    setIsPopupOpen(true);
  };

  const handleChoosePlanBuyer = (plan) => {
    if (!userData) {
      handleOpen();
    } else {
      handleChoosePlan(plan, "buyer");
    }
  };
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleChoosePlanSeller = (plan) => {
    if (!userData) {
      alert("log in first");
    } else {
      handleChoosePlan(plan, "seller");
    }
  };

  const handlePayNow = async (plan, type) => {
    try {
      const userId = userData?._id;
      const planId = plan._id;

      if (type === "buyer") {
        const response = await fetch(`${url}/api/buy-plan`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId, planId }),
        });

        if (response.ok) {
          const data = await response.json();
          setUserData(data.user);
        } else {
          const errorData = await response.json();
          alert("Failed to purchase the plan.");
        }
      } else if (type === "seller") {
        const response = await fetch(`${url}/api/buy-seller-plan`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: userData._id,
            planId: plan._id,
          }),
        });
        const data = await response.json();

        setUserData(data.user);
      }
    } catch (error) {
      alert("An error occurred while purchasing the plan.");
    }
  };

  return (
    <Box
      sx={{ background: "#FFFFFF", minHeight: "100vh", width: "100%" }}
    >
      {isBuyer && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "14px",
            }}
          >
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontSize: isMobile ? 24 : 35,
                fontWeight: 600,
                color: colors.primary_dark,
                textAlign: "center",
                paddingX: 0,
                textDecoration: "underline",
                textDecorationThickness: "3px", // Controls underline thickness
                textUnderlineOffset: "8px", // Controls distance from text
              }}
            >
              {t("planTitle")}
            </Typography>
          </Box>

          {buyerPlans.length === 0 ? (
            // Skeleton for 3 placeholder cards
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: isMobile ? 2 : 5,
                marginTop: 5,
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {Array.from({ length: 3 }).map((_, index) => (
                <Paper
                  key={index}
                  sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "24px",
                    border: "1px solid green",
                    background: theme.palette.background.paper,
                    boxShadow: 10,
                    width: isMobile ? "75%" : "30%",
                    textAlign: "start",
                  }}
                >
                  {/* Skeleton for Plan Name */}
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={40}
                    sx={{ marginBottom: 3 }}
                  />

                  {/* Skeleton for Plan Details */}
                  <Box
                    sx={{
                      marginBottom: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>

                  {/* Skeleton for Price */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Skeleton variant="text" width="40%" height={30} />
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={20}
                      sx={{ marginLeft: 1 }}
                    />
                  </Box>

                  {/* Skeleton for Button */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: isMobile ? 3 : 5,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={40}
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            // Actual Buyer Plans
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: isMobile ? 2 : 5,
                marginTop: 5,
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {buyerPlans.map((plan) => (
                <Paper
                  key={plan._id}
                  sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "24px",
                    border: "1px solid green",
                    background: theme.palette.background.paper,
                    boxShadow: 10,
                    width: isMobile ? "75%" : "30%",
                    textAlign: "start",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: isMobile ? 24 : 30,
                      fontWeight: 600,
                      marginBottom: 3,
                      color: colors.primary_dark,
                    }}
                  >
                    {plan.name}
                  </Typography>
                  <Box
                    sx={{
                      marginBottom: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      fontFamily: fonts.poppins,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? 16 : 20,
                        fontFamily: fonts.poppins,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t("contacts")}{" "}
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: fonts.poppins,
                          fontWeight: 600,
                          fontSize: isMobile ? 18 : 22,
                          color: colors.primary_dark,
                        }}
                      >
                        {plan.contacts}
                      </Typography>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? 20 : 25,
                        fontWeight: 600,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        marginTop: 1,
                        fontFamily: fonts.poppins,
                        color: colors.primary_dark,
                      }}
                    >
                      ₹{plan.discountedPrice}
                      <Typography
                        sx={{
                          fontFamily: fonts.poppins,
                          textDecoration: "line-through",
                          color: "gray",
                          fontSize: isMobile ? 14 : 16,
                        }}
                      >
                        ₹{plan.originalPrice}
                      </Typography>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: isMobile ? 3 : 5,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        width: "100%",
                        backgroundImage: colors.button,
                        borderRadius: 2,
                        fontFamily: fonts.poppins,
                        padding: "8px 20px",
                        fontSize: isMobile ? 14 : size.formLabel,
                        textTransform: "none",
                        color: theme.palette.common.white,
                      }}
                      onClick={() => handleChoosePlanBuyer(plan)}
                    >
                      {t("choose")}
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {isSeller && (
        <Box>
          {/* Title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "14px",
            }}
          >
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontSize: isMobile ? 24 : 35,
                fontWeight: 600,
                color: colors.primary_dark,
                textAlign: "center",
                paddingX: isMobile ? 2 : 0,
                textDecoration: "underline",
                textDecorationThickness: "3px", // Controls underline thickness
                textUnderlineOffset: "8px", // Controls distance from text
                marginBottom: 5,
              }}
            >
              {t("planTitle")}
            </Typography>
          </Box>

          {/* Plan Cards */}
          {sellerPlans.length === 0 ? (
            // Skeleton for 3 placeholder cards
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: isMobile ? 2 : 5,
                marginTop: 5,
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {Array.from({ length: 3 }).map((_, index) => (
                <Paper
                  key={index}
                  sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "24px",
                    border: "1px solid green",
                    background: "#FFFFFF",
                    boxShadow: 10,
                    width: isMobile ? "75%" : "30%",
                    textAlign: "start",
                  }}
                >
                  {/* Skeleton for Plan Name */}
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={40}
                    sx={{ marginBottom: 3 }}
                  />

                  {/* Skeleton for Sponsored Hours */}
                  <Box
                    sx={{
                      marginBottom: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>

                  {/* Skeleton for Pricing */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Skeleton variant="text" width="40%" height={30} />
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={20}
                      sx={{ marginLeft: 1 }}
                    />
                  </Box>

                  {/* Skeleton for Choose Plan Button */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: isMobile ? 3 : 5,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={40}
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            // Actual Seller Plans
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: isMobile ? 2 : 5,
                marginTop: 5,
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {sellerPlans.map((plan) => (
                <Paper
                  key={plan._id}
                  sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "24px",
                    border: "1px solid green",
                    background: "#FFFFFF",
                    boxShadow: 10,
                    width: isMobile ? "75%" : "30%",
                    textAlign: "start",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: isMobile ? 24 : 30,
                      fontWeight: 600,
                      marginBottom: 3,
                      color: colors.primary_dark,
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Sponsored Hours */}
                  <Box
                    sx={{
                      marginBottom: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      fontFamily: fonts.poppins,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? 16 : 20,
                        fontWeight: 500,
                        fontFamily: fonts.poppins,
                        color: colors.dark,
                      }}
                    >
                      {t("hours")}{" "}
                      <Typography
                        component="span"
                        sx={{
                          color: colors.primary_dark,
                          fontFamily: fonts.poppins,
                          fontWeight: 600,
                          fontSize: isMobile ? 18 : 22,
                        }}
                      >
                        {plan.sponseredHrs}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Pricing */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? 20 : 25,
                        fontWeight: 600,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        marginTop: 1,
                        fontFamily: fonts.poppins,
                        color: colors.primary_dark,
                      }}
                    >
                      ₹{plan.discountedPrice}
                      <Typography
                        sx={{
                          fontFamily: fonts.poppins,
                          textDecoration: "line-through",
                          color: "gray",
                          fontSize: isMobile ? 14 : size.formLabel,
                        }}
                      >
                        ₹{plan.originalPrice}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Choose Plan Button */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: isMobile ? 3 : 5,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        width: "100%",
                        backgroundImage: colors.button,
                        borderRadius: 2,
                        padding: "8px 20px",
                        fontSize: isMobile ? 14 : size.formLabel,
                        textTransform: "none",
                        color: colors.light,
                      }}
                      onClick={() => handleChoosePlanSeller(plan)}
                    >
                      {t("choose")}
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}
      {isPopupOpen && selectedPlan && (
        <PaymentPopup
          plan={selectedPlan}
          onClose={() => setIsPopupOpen(false)}
          onPayNow={() =>
            handlePayNow(selectedPlan, isBuyer ? "buyer" : "seller")
          }
          userData={userData}
        />
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "90%", sm: "70%", md: "50%" }, // Responsive width
            maxWidth: "600px", // Limits width on larger screens
            height: "auto", // Auto height adjustment
            maxHeight: "90vh", // Prevent overflow on tall screens
            margin: "auto",
            overflow: "auto", // Enables scroll if content overflows
            borderRadius: "24px",
            position: "relative", // Needed for absolute positioning of the close button
          },
        }}
      >
        {/* Close Icon Button */}
        <IconButton
          aria-label="close"
          onClick={handleClose} // Close the dialog on click
          sx={{
            position: "absolute", // Position the button absolutely
            right: 10, // Distance from the right edge
            top: 10, // Distance from the top edge
            zIndex: 1, // Ensure it's above other content
            color: "gray", // Customize the icon color
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)", // Hover effect
            },
          }}
        >
          <Close />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            marginTop: isMobile ? 3 : 5,
            textAlign: "center",
            padding: isMobile ? "10px" : "20px",
          }}
        >
          <img
            src="/Icon krushisetu.png"
            alt="logo"
            style={{
              width: isMobile ? 80 : 120, // Adjusted size for better fit with the new icon
              objectFit: 'contain'
            }}
          />
          <Typography
            sx={{
              fontFamily: fonts.poppins,
              fontWeight: 600,
              fontSize: isMobile ? 24 : 30, // Smaller font size on mobile
              color: colors.primary_dark,
              marginTop: isMobile ? 1 : 2,
            }}
          >
            {t("t&cWelcome")}
          </Typography>
          <Typography
            sx={{
              fontFamily: fonts.poppins,
              fontSize: isMobile ? 14 : size.subtitle, // Smaller font size on mobile
              marginTop: isMobile ? 0.5 : 1,
            }}
          >
            {t("home-title")}
          </Typography>
        </Box>

        <DialogContent sx={{ overflowY: "auto", maxHeight: "70vh" }}>
          <SignUp /> {/* Render Signup inside popup */}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PlansPricing;
